import { parseJwt, refreshToken } from '@realadvisor/auth-client';

type JwtPayload = Record<string, unknown>;

const getOnboardingJwt = (): JwtPayload | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const token = window.localStorage.getItem('access_token');
  if (token == null) {
    return null;
  }
  try {
    return parseJwt(token) as JwtPayload;
  } catch {
    return null;
  }
};

export const getOnboardingUserIdFromJwt = (): string => {
  const decoded = getOnboardingJwt();
  if (decoded == null) {
    return '';
  }
  try {
    const user = decoded.user as Record<string, unknown> | undefined;
    const userId = user?.id;
    if (typeof userId === 'string' && userId !== '') {
      return userId;
    }
    const claims = decoded['https://hasura.io/jwt/claims'] as
      | Record<string, unknown>
      | undefined;
    const claimUserId = claims?.['x-hasura-user-id'];
    return typeof claimUserId === 'string' ? claimUserId : '';
  } catch {
    return '';
  }
};

export const getOnboardingTenantIdFromJwt = (): string | null => {
  const decoded = getOnboardingJwt();
  if (decoded == null) {
    return null;
  }
  try {
    const user = decoded.user as { tenantId?: unknown } | undefined;
    const claims = decoded['https://hasura.io/jwt/claims'] as
      | Record<string, unknown>
      | undefined;
    const tenantId = user?.tenantId ?? claims?.['x-hasura-tenant-id'];
    return typeof tenantId === 'string' ? tenantId : null;
  } catch {
    return null;
  }
};

export const jwtGrantsTeam = (teamId: string): boolean => {
  if (teamId === '') {
    return false;
  }
  const decoded = getOnboardingJwt();
  if (decoded == null) {
    return false;
  }
  try {
    const claims = decoded['https://hasura.io/jwt/claims'] as
      | Record<string, unknown>
      | undefined;
    const teamsIds = claims?.['x-hasura-teams-ids'];
    // Hasura serializes text[] session variables as a Postgres array literal
    // ("{uuid,uuid}"), not a JSON array — see auth/refresh-token.ts.
    return (
      typeof teamsIds === 'string' &&
      teamsIds
        .replace(/^\{|\}$/g, '')
        .split(',')
        .includes(teamId)
    );
  } catch {
    return false;
  }
};

// Onboarding mints the broker's team server-side, but the caller's JWT was
// issued before it existed (default role 'user' cannot read teams). Re-mint the
// token so team-scoped queries resolve. No-op once the claim is present.
export const ensureSessionGrantsTeam = async (
  teamId: string,
): Promise<boolean> => {
  if (teamId === '' || jwtGrantsTeam(teamId)) {
    return teamId !== '';
  }
  try {
    await refreshToken();
  } catch {
    // network failure — handled by the post-refresh check below
  }
  return jwtGrantsTeam(teamId);
};

export const getAgentNamesFromJwt = (): {
  first_name: string;
  last_name: string;
} => {
  const decoded = getOnboardingJwt();
  if (decoded == null) {
    return { first_name: '', last_name: '' };
  }
  try {
    const claims = decoded['https://hasura.io/jwt/claims'] as
      | Record<string, unknown>
      | undefined;

    const rawFirst = claims?.['x-hasura-user-first-name'];
    let first_name = typeof rawFirst === 'string' ? rawFirst.trim() : '';

    const rawLast = claims?.['x-hasura-user-last-name'];
    let last_name = typeof rawLast === 'string' ? rawLast.trim() : '';

    const user = decoded.user as Record<string, unknown> | undefined;
    const display = (user?.displayName as string | undefined)?.trim() ?? '';
    if (display !== '') {
      const parts = display.split(/\s+/);
      if (first_name.length === 0 && parts[0]) {
        first_name = parts[0];
      }
      if (last_name.length === 0 && parts.length > 1) {
        last_name = parts.slice(1).join(' ');
      }
    }

    return { first_name, last_name };
  } catch {
    return { first_name: '', last_name: '' };
  }
};
